<template>
  <div id="app">
    <div class="modal d-block modal-lg modalBackground">
      <div class="modal-dialog" role="document">
        <div class="modal-content border-0">
          <div class="modal-header">
            <h5 class="modal-title">{{ title }}</h5>
            <button type="button" class="btn-close" @click.stop="close()">
            </button>
          </div>
          <div class="modal-body">
            <SystemAlert :message="responseMessage" :type="responseType" />
            <form>
              <div class="row">
                <div class="col-md-6">
                  <label for="name">Nome</label>
                  <input type="text" class="form-control mb-3" id="name" name="name" v-model="name"
                         placeholder="Digite o nome">
                </div>
                <div class="col-md-6">
                  <label for="email">Email</label>
                  <input type="text" class="form-control mb-3" id="email" name="email" v-model="email"
                         placeholder="Digite o email">
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <label for="password_confirmation">Senha</label>
                  <div class="password-input">
                    <input :type="passwordVisible ? 'text' : 'password'" class="form-control mb-3" id="password"
                           name="password" v-model="password" placeholder="Digite a senha">
                  </div>
                  <p class="password-rules">Mínimo 6 caracteres</p>
                </div>
                <div class="col-md-6">
                  <label for="password_confirmation">Confirmar Senha</label>
                  <div class="password-input">
                    <input :type="passwordVisible ? 'text' : 'password'" class="form-control mb-3"
                           id="password_confirmation" name="password_confirmation" v-model="password_confirmation"
                           placeholder="Digite a senha">
                  </div>
                </div>
              </div>

              <div class="form-check form-switch mb-2">
                <input v-model="userTest" class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault">
                <label class="form-check-label me-2" for="flexSwitchCheckDefault">Usuario Teste</label>
                <i 
                  ref="questionIconReference" 
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-bs-custom-class="custom-tooltip"
                  title="Ao selecionar esta opção, o usuário será classificado como 'usuário de testes', ficando oculto 
                    do ranking de alunos, das notas e das listagens de disciplinas exibidas para professores, tutores e
                    gestores do sistema. Embora esse tipo de usuário tenha acesso completo a todas as funcionalidades e 
                    atividades, ele permanecerá invisível para métricas e relatórios gerenciais."
                  class="bi question-icon cursor-pointer bi-question-circle-fill" />
              </div>

              <div v-if="!updatedCompanyView" class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <label for="role">Permissão</label>
                    <select v-if="context === 'create'" class="form-control" id="role" v-model="role">
                      <option value="" disabled>Selecione uma permissão</option>
                      <option v-for="r in availableRoles" :key="r.value" :value="r.value">{{ r.label }}</option>
                    </select>
                    <input v-else type="text" class="form-control read-only-input" id="role" :value="roleLabel" readonly>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary d-flex align-items-center justify-content-center"
                    @click.stop="save">
              <span :class="showLoader ? 'mr-2' : ''">{{ buttonTitle }}</span>
            </button>
            <button type="button" class="btn btn-secondary" @click.stop="close()">Fechar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useStore, mapGetters } from 'vuex';
import SystemAlert from '../alerts/SystemAlert.vue';
import { Tooltip } from 'bootstrap';

export default {
  name: 'CreateOrUpdateUserModal',

  props: {
    showModal: Boolean,
    title: String,
    userData: Object,
    updatedCompanyView: Boolean,
    activeModalName: {
      type: String,
      required: true
    },
  },

  data() {
    return {
      store: useStore(),
      name: '',
      email: '',
      password: '',
      userTest: false,
      password_confirmation: '',
      buttonTitle: 'Salvar',
      context: 'create',
      role: '',
      passwordVisible: false,
      alertMessage: "",
      alertType: "",
      responseMessage: "",
      responseType: "",
      tooltipReference: null,
      allRoles: [
        { value: 'admin', label: 'Administrador' },
        { value: 'manager', label: 'Gestor' },
        { value: 'supervisor', label: 'Supervisor' },
        { value: 'client', label: 'Cliente' },
        { value: 'teacher', label: 'Professor' },
        { value: 'tutor', label: 'Tutor' },
        { value: 'student', label: 'Aluno' },
      ]
    };
  },

  components: {
    SystemAlert,
  },

  watch: {
    userData: {
      handler(newData) {
        this.name = newData?.name ?? '';
        this.email = newData?.email ?? '';
        this.buttonTitle = newData?.name ? 'Atualizar' : 'Salvar';
        this.context = newData?.name ? 'update' : 'create'
        this.password = '';
        this.password_confirmation = '';
        this.userTest = Boolean(newData?.test_user)
      },
      immediate: true
    }
  },

  computed: {
    ...mapGetters([
      'getToken',
      'getType'
    ]),

    availableRoles() {
      const creatable = this.allRoles.filter(r => r.value !== 'teacher');
     
      return this.getType === 'admin'
        ? creatable
        : creatable.filter(r => r.value !== 'admin');
    },

    roleLabel() {
      const t = this.userData?.type;
      const labelOf = (v) => this.allRoles.find(r => r.value === v)?.label || 'Usuário';
      return Array.isArray(t) ? t.map(labelOf).join(', ') : labelOf(t);
    }
  },

  mounted() {
    this.initializeTooltip()
  },

  beforeUnmount() {
    this.destroyTooltip()
  },

  methods: {
    initializeTooltip() {
      this.$nextTick(() => {
        const tooltipElement = this.$refs.questionIconReference
        if (tooltipElement && !this.tooltipReference) {
            this.tooltipReference = new Tooltip(tooltipElement, {
              customClass: 'custom-tooltip',
            })
        }
      })
    },

    destroyTooltip() {
      if (!this.tooltipReference) return
      
      this.tooltipReference.dispose()
    },

    close(updatedUser) {
      this.$emit('close', updatedUser);
    },

    save() {
      return this.context === 'create' ? this.createUser() : this.updateUser();
    },

    togglePasswordVisibility() {
      this.passwordVisible = !this.passwordVisible;
    },

    async createUser() {
      if (!this.role) {
        this.store.dispatch('toastfy/activeErrorNotification', { text: 'Selecione uma permissão para o usuário.' });
        return;
      }

      this.$emit('hideOrShowLoader', true);
      const user = {
        name: this.name,
        email: this.email,
        password: this.password,
        password_confirmation: this.password_confirmation,
        test_user: this.userTest,
        role: this.role
      }

      try {
        const newUser = await this.store.dispatch('createUser', {
          user: user,
          userToken: this.getToken
        });

        console.log("Usuário criado com sucesso:", newUser);

        this.$emit('close', newUser);
      } catch (error) {
        if (error.response && error.response.status === 422) {
          console.log("Erro ao adicionar usuário", error.response.data.message);
        } else {
          console.log("Erro ao adicionar usuário", error);
        }
      } finally {
        this.$emit('hideOrShowLoader', false);
      }
    },

    async updateUser() {
      const payload = {
        user: {
          userId: this.userData.id,
          test_user: this.userTest,
          ...this.name && { name: this.name },
          ...this.email && { email: this.email },
          ...this.password && { password: this.password },
          ...this.password_confirmation && { password_confirmation: this.password_confirmation }
        },
        userToken: this.getToken
      }

      try {
        const updatedUser = await this.store.dispatch('updateUser', payload);

        this.$emit('close', updatedUser);
      } catch (error) {
        if (error.response && error.response.status === 422) {
          console.log("Erro ao atualizar usuário", error.response.data.message);
        } else {
          console.log("Erro ao adicionar usuário", error);
        }
      } finally {
        this.$emit('hideOrShowLoader', false);
      }
    },
  }

}
</script>

<style>
.custom-tooltip {
  --bs-tooltip-bg: var(--gray-3) !important;
  --bs-tooltip-max-width: 500px !important;
  --bs-tooltip-padding-y: 10px;
  --bs-tooltip-opacity: 1;
  text-align: left !important;
  word-wrap: break-word !important;
  white-space: normal !important;
}
</style>

<style scoped>
.question-icon {
  font-size: calc(14px * var(--font-scale));
}

#app {
  display: flex;
  justify-content: flex-end;
  padding: 10px;
}

.button-container {
  margin-left: auto;
}

.password-rules {
  font-size: calc(12px * var(--font-scale));
  color: var(--gray-3);
}

.modal-title {
  color: var(--gray-2);
}

.btn-primary {
  background-color: var(--blue-0);
}

.password-input {
  position: relative;
}

.password-input i {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
}

.form-role {
  border: none;
  border-radius: 10px;
}

.read-only-input {
  opacity: 0.6;
  pointer-events: none;
}

.subtitle {
  font-size: calc(14px * var(--font-scale));
  color: var(--gray-2);
}

.mr-2 {
  margin-right: 0.5rem;
}
</style>